import { spanFromRegExpMatch } from '../spans';
import type { ExtractorOutput } from './types';

/**
 * Parse a maintenance/HOA dollar amount. Unlike listing price, bare numbers are
 * dollars as-written ("500" → 500), not thousands.
 */
function parseFeeAmount(raw: string, unitHint?: string): number {
  const value = parseFloat(raw.replace(/,/g, ''));
  const unit = (unitHint || '').toLowerCase();
  if (unit === 'm') return value * 1_000_000;
  if (unit === 'k') return value * 1_000;
  return value;
}

const FEE_NUMBER = '\\$?\\s*(\\d+(?:[.,]\\d+)?)\\s*([km])?';
const FEE_LABEL =
  '(?:maintenance(?:\\s+fees?)?|hoa(?:\\s+fees?)?|condo\\s+fees?|strata(?:\\s+fees?)?)';

/**
 * Max maintenance / HOA fee. Requires an explicit fee keyword so price phrases
 * like "under $500k" are not misread as maintenance.
 */
export function extractMaintenanceFee(query: string): ExtractorOutput {
  const lowMatch = /\blow\s+maintenance(?:\s+fees?)?\b/gi.exec(query);
  if (lowMatch) {
    const maintenanceFee = 300;
    return {
      partial: {
        additional: { condoOrTownhouse: { maintenanceFee } },
      },
      spans: [
        spanFromRegExpMatch(query, lowMatch, 'additional.condoOrTownhouse.maintenanceFee', maintenanceFee, {
          confidence: 0.7,
          ruleId: 'maintenanceFee.low-default',
        }),
      ],
    };
  }

  const patterns: Array<{ pattern: RegExp; ruleId: string }> = [
    {
      pattern: new RegExp(
        `${FEE_LABEL}\\s*(?:of\\s+)?(?:under|below|less\\s+than|max(?:imum)?|up\\s+to|<=?)\\s*${FEE_NUMBER}`,
        'i'
      ),
      ruleId: 'maintenanceFee.label-then-max',
    },
    {
      pattern: new RegExp(
        `(?:under|below|less\\s+than|max(?:imum)?|up\\s+to|<=?)\\s*${FEE_NUMBER}\\s*(?:(?:per\\s+month|\\/mo|monthly)\\s+)?${FEE_LABEL}`,
        'i'
      ),
      ruleId: 'maintenanceFee.max-then-label',
    },
    {
      pattern: new RegExp(
        `(?:max(?:imum)?\\s+)?${FEE_LABEL}\\s*(?:of\\s+|is\\s+|at\\s+)?${FEE_NUMBER}`,
        'i'
      ),
      ruleId: 'maintenanceFee.label-amount',
    },
  ];

  for (const { pattern, ruleId } of patterns) {
    const match = pattern.exec(query);
    if (!match || match.index === undefined) continue;
    const maintenanceFee = parseFeeAmount(match[1], match[2]);
    if (!Number.isFinite(maintenanceFee) || maintenanceFee <= 0) continue;

    return {
      partial: {
        additional: { condoOrTownhouse: { maintenanceFee } },
      },
      spans: [
        spanFromRegExpMatch(query, match, 'additional.condoOrTownhouse.maintenanceFee', maintenanceFee, {
          confidence: 0.9,
          ruleId,
        }),
      ],
    };
  }

  return { partial: {}, spans: [] };
}

/**
 * Locker / storage locker presence for condo & townhouse filters.
 * "with locker" / "has locker" → yes; "no locker" / "without locker" → no.
 */
export function extractLocker(query: string): ExtractorOutput {
  const noPattern = /\b(?:no|without|w\/o)\s+(?:a\s+)?(?:storage\s+)?lockers?\b/gi;
  const noMatch = noPattern.exec(query);
  if (noMatch) {
    return {
      partial: {
        additional: { condoOrTownhouse: { locker: 'no' } },
      },
      spans: [
        spanFromRegExpMatch(query, noMatch, 'additional.condoOrTownhouse.locker', 'no', {
          confidence: 0.95,
          ruleId: 'locker.no',
        }),
      ],
    };
  }

  const yesPattern =
    /\b(?:with|has|have|including|includes|and)\s+(?:a\s+)?(?:storage\s+)?lockers?\b|\b(?:storage\s+)?lockers?\b/gi;
  const yesMatch = yesPattern.exec(query);
  if (yesMatch) {
    return {
      partial: {
        additional: { condoOrTownhouse: { locker: 'yes' } },
      },
      spans: [
        spanFromRegExpMatch(query, yesMatch, 'additional.condoOrTownhouse.locker', 'yes', {
          confidence: 0.9,
          ruleId: 'locker.yes',
        }),
      ],
    };
  }

  return { partial: {}, spans: [] };
}
